import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import {
  Context,
  createContext,
  FC,
  memo,
  ReactNode,
  useContext,
  useLayoutEffect,
  useMemo,
  useReducer,
  useState
} from 'react';
import { useLocation } from 'react-router';
import { fiveZeroZeroErrorMessage } from 'src/constants/messages';
import { AllocateHistoryEntity } from 'src/entities/AllocateHistory.entity';
import { OrderSearchConditionEntity } from 'src/entities/OrderSearchCondition.entity';
import { PlanningEntity } from 'src/entities/planningEntity';
import { PlanningConditions } from 'src/entities/PlanningHistory.entity';
import { PlanningResponseEntity, PlanningResponseV5Entity } from 'src/entities/planningResponseEntity';
import { PlanningsDeliveryEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsGroupEntity } from 'src/entities/PlanningsGroup.entity';
import { NotAllocReasonState, PlanningsNotAllocReason } from 'src/entities/PlanningsNotAllocReasons.entity';
import { PlanningsOperationDeliveryByDeliveryIdEntity } from 'src/entities/PlanningsOperationEntitiesWithStatsByDeliveryId.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { SelectedCycle } from 'src/entities/SelectedCycle.entity';
import { usePlanningsDeliveriesCache, usePlanningsDeliveriesWithLegRunsCache } from 'src/hooks/usePlanningsCache';
import { PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY } from 'src/hooks/useQueryPlanningsDeliveries';
import arrayUtil from 'src/utils/array.util';
import { LogicalOperatorVo } from 'src/vo/LogicalOperator.vo';
import { SearchOperatorVo } from 'src/vo/SearchOperator.vo';
import { TruckDisplayStatusVo } from 'src/vo/TruckDisplayStatusVo';

const searchConditionInitialValue: OrderSearchConditionEntity = {
  logicalOperator: 'or',
  key: undefined,
  operator: undefined,
  value: undefined,
};

type ContextEntity = {
  startOn: string;
  endOn: string;
  isLoading: boolean;
  deliveryEntities: PlanningsDeliveryEntity[];
  calculating: boolean;
  selectedCycleIndexes: SelectedCycle[];
  truckDisplayStatus: TruckDisplayStatusVo;
  selectedGroupEntities: PlanningsGroupEntity[];
  allDeliverySelected: boolean;
  orderSearchKw: string;
  currentlyOrderSearching: boolean;
  orderSearchConditions: OrderSearchConditionEntity[];
  planningEntity: PlanningEntity;
  latestAlgorithmRequestVersion: number;
  planningsOperationDeliveryByDeliveryIdEntity: PlanningsOperationDeliveryByDeliveryIdEntity;
  orderIds: number[];
  allocatedOrderIds: number[];
  selectedOrderIds: number[];
  notAllocState: NotAllocReasonState;
  driverEntities: PlanningsDriverEntity[];
  truckEntities: PlanningsTruckEntity[];
  notAllocReasons: PlanningsNotAllocReason[];
  allocateHistories: AllocateHistoryEntity[];
};

type SetStartOn = {
  type: 'SET_START_ON';
  value: string;
};
type SetEndOn = {
  type: 'SET_END_ON';
  value: string;
};
type SetInitialState = {
  type: 'SET_INITIAL_STATE';
};
type SetIsLoading = {
  type: 'SET_IS_LOADING';
  value: boolean;
}
type SetDeliveryEntities = {
  type: 'SET_DELIVERY_ENTITIES';
  value: PlanningsDeliveryEntity[];
}
type SetCalculating = {
  type: 'SET_CALCULATING';
  value: boolean;
}
type UpdateCycleIndex = {
  type: 'UPDATE_CYCLE_INCEX';
  value: SelectedCycle;
}
type RemoveCycleIndex = {
  type: 'REMOVE_CYCLE_INDEX';
  value: number;
}
type ResetCycleIndexes = {
  type: 'RESET_CYCLE_INDEXES';
}
type SetTruckDisplayStatus = {
  type: 'SET_TRUCK_DISPLAY_STATUS';
  value: TruckDisplayStatusVo;
}
type SetSelectedGroupEntities = {
  type: 'SET_SELECTED_GROUP_ENTITIES';
  value: PlanningsGroupEntity[];
}
type ResetSelectedGroupEntities = {
  type: 'RESET_SELECTED_GROUP_ENTITIES';
}
type SetAllDeliverySelected = {
  type: 'SET_ALL_DELIVERY_SELECTED';
  value: boolean;
}
type SetOrderSearchKw = {
  type: 'SET_ORDER_SEARCH_KW';
  value: string;
}
type SetCurrentlyOrderSearching = {
  type: 'SET_CURRENTLY_ORDER_SEARCHING';
  value: boolean;
}
type UpdateOrderSearchConditionLogicalOperator = {
  type: 'UPDATE_ORDER_SEARCH_CONDITION_LOGICAL_OPERATOR';
  index: number;
  logicalOperator: LogicalOperatorVo;
}
type UpdateOrderSearchConditionSearchKey = {
  type: 'UPDATE_ORDER_SEARCH_CONDITION_SEARCH_KEY';
  index: number;
  key: string | undefined;
}
type UpdateOrderSearchConditionSearchOperator = {
  type: 'UPDATE_ORDER_SEARCH_CONDITION_SEARCH_OPERATOR';
  index: number;
  operator: SearchOperatorVo | undefined;
}
type UpdateOrderSearchConditionSearchValue = {
  type: 'UPDATE_ORDER_SEARCH_CONDITION_SEARCH_VALUE';
  index: number;
  value: string | undefined;
}
type AddOrderSearchConditions = {
  type: 'ADD_ORDER_SEARCH_CONDITIONS';
}
type DeleteOrderSearchConditions = {
  type: 'DELETE_ORDER_SEARCH_CONDITIONS';
  index: number;
}
type SetOrderSearchConditions = {
  type: 'SET_ORDER_SEARCH_CONDITIONS';
  value: OrderSearchConditionEntity[];
}
type SetPlanningEntity = {
  type: 'SET_PLANNING_ENTITY';
  value: PlanningEntity;
}
type SetLatestAlgorithmRequestVersion = {
  type: 'SET_LATEST_ALGORITHM_REQUEST_VERSION';
  value: number;
}
type SetPlanningsOperationDeliveryByDeliveryIdEntity = {
  type: 'SET_PLANNINGS_OPERATION_DELIVERY_BY_DELIVERY_ID_ENTITY';
  value: PlanningsOperationDeliveryByDeliveryIdEntity;
}
type AddEmptyCyclePlanningsOperationDeliveryByDeliveryIdEntity = {
  type: 'ADD_EMPTY_CYCLE_PLANNINGS_OPERATION_DELIVERY_BY_DELIVERY_ID_ENTITY';
  value: number; // deliveryId
}
type RemoveEmptyCyclePlanningsOperationDeliveryByDeliveryIdEntity = {
  type: 'REMOVE_EMPTY_CYCLE_PLANNINGS_OPERATION_DELIVERY_BY_DELIVERY_ID_ENTITY';
  deliveryId: number;
  cycleIndex: number;
}
type SetOrderIds = {
  type: 'SET_ORDER_IDS';
  value: number[];
}
type AddOrderIds = {
  type: 'ADD_ORDER_IDS';
  value: number[];
}
type RemoveOrderIds = {
  type: 'REMOVE_ORDER_IDS';
  value: number[];
}
type ResetOrderIds = {
  type: 'RESET_ORDER_IDS';
}
type SetAllocatedOrderIds = {
  type: 'SET_ALLOCATED_ORDER_IDS';
  value: number[];
}
type AddAllocatedOrderIds = {
  type: 'ADD_ALLOCATED_ORDER_IDS';
  value: number[];
}
type RemoveAllocatedOrderIds = {
  type: 'REMOVE_ALLOCATED_ORDER_IDS';
  value: number[];
}
type ResetAllocatedOrderIds = {
  type: 'RESET_ALLOCATED_ORDER_IDS';
}
type SetSelectedOrderIds = {
  type: 'SET_SELECTED_ORDER_IDS';
  value: number[];
}
type AddSelectedOrderIds = {
  type: 'ADD_SELECTED_ORDER_IDS';
  value: number;
}
type BulkAddSelectedOrderIds = {
  type: 'BULK_ADD_SELECTED_ORDER_IDS';
  value: number[];
}
type RemoveSelectedOrderIds = {
  type: 'REMOVE_SELECTED_ORDER_IDS';
  value: number;
}
type BulkRemoveSelectedOrderIds = {
  type: 'BULK_REMOVE_SELECTED_ORDER_IDS';
  value: number[];
}
type ResetSelectedOrderIds = {
  type: 'RESET_SELECTED_ORDER_IDS';
}
type SetNotAllocReasonState = {
  type: 'SET_NOT_ALLOC_REASON_STATE';
  value: NotAllocReasonState;
}
type SetDriverEntities = {
  type: 'SET_DRIVER_ENTITIES';
  value: PlanningsDriverEntity[];
}
type SetTruckEntities = {
  type: 'SET_TRUCK_ENTITIES';
  value: PlanningsTruckEntity[];
}
type SetPlanningsNotAllocReasons = {
  type: 'SET_PLANNINGS_NOT_ALLOC_REASONS';
  value: PlanningsNotAllocReason[];
}
type ResetPlanningsNotAllocReasons = {
  type: 'RESET_PLANNINGS_NOT_ALLOC_REASONS';
}
type AddAllocateHistoryEntity = {
  type: 'ADD_ALLOCATE_HISTORY_ENTITY';
  value: AllocateHistoryEntity;
}
type ResetAllocateHistoryEntity = {
  type: 'RESET_ALLOCATE_HISTORY_ENTITY';
}

type Action =
  | SetStartOn
  | SetEndOn
  | SetDeliveryEntities
  | SetInitialState
  | SetCalculating
  | UpdateCycleIndex
  | RemoveCycleIndex
  | ResetCycleIndexes
  | SetTruckDisplayStatus
  | SetSelectedGroupEntities
  | ResetSelectedGroupEntities
  | SetAllDeliverySelected
  | SetOrderSearchKw
  | SetCurrentlyOrderSearching
  | UpdateOrderSearchConditionLogicalOperator
  | UpdateOrderSearchConditionSearchKey
  | UpdateOrderSearchConditionSearchOperator
  | UpdateOrderSearchConditionSearchValue
  | AddOrderSearchConditions
  | DeleteOrderSearchConditions
  | SetOrderSearchConditions
  | SetPlanningEntity
  | SetLatestAlgorithmRequestVersion
  | SetPlanningsOperationDeliveryByDeliveryIdEntity
  | AddEmptyCyclePlanningsOperationDeliveryByDeliveryIdEntity
  | RemoveEmptyCyclePlanningsOperationDeliveryByDeliveryIdEntity
  | SetOrderIds
  | AddOrderIds
  | RemoveOrderIds
  | ResetOrderIds
  | SetAllocatedOrderIds
  | AddAllocatedOrderIds
  | RemoveAllocatedOrderIds
  | ResetAllocatedOrderIds
  | SetSelectedOrderIds
  | AddSelectedOrderIds
  | BulkAddSelectedOrderIds
  | RemoveSelectedOrderIds
  | BulkRemoveSelectedOrderIds
  | ResetSelectedOrderIds
  | SetNotAllocReasonState
  | SetDriverEntities
  | SetTruckEntities
  | SetPlanningsNotAllocReasons
  | ResetPlanningsNotAllocReasons
  | AddAllocateHistoryEntity
  | ResetAllocateHistoryEntity
  | SetIsLoading;

type ContextValueEntity = ContextEntity & {
  reset: () => void;
  setStartOn: (on: string) => void;
  setEndOn: (on: string) => void;
  setIsLoading: (value: boolean) => void;
  setDeliveryEntities: (value: PlanningsDeliveryEntity[]) => void;
  setCalculating: (value: boolean) => void;
  updateCycleIndex: (value: SelectedCycle) => void;
  removeCycleIndex: (deliveryId: number) => void;
  resetCycleIndexes: () => void;
  setTruckDisplayStatus: (value: TruckDisplayStatusVo) => void;
  setSelectedGroupEntities: (value: PlanningsGroupEntity[]) => void;
  resetSelectedGroupEntities: () => void;
  setAllDeliverySelected: (value: boolean) => void;
  setOrderSearchKw: (value: string) => void;
  setCurrentlyOrderSearching: (value: boolean) => void;
  updateSearchConditionLogicalOperator: (index: number, logicalOperator: LogicalOperatorVo) => void;
  updateSearchConditionSearchKey: (index: number, key: string | undefined) => void;
  updateSearchConditionSearchOperator: (index: number, operator: SearchOperatorVo | undefined) => void;
  updateSearchConditionSearchValue: (index: number, value: string | undefined) => void;
  addSearchConditionSearchConditions: () => void;
  deleteSearchConditions: (index: number) => void;
  setSearchConditions: (value: OrderSearchConditionEntity[]) => void;
  planningSettings: () => PlanningConditions;
  setPlanningEntity: (value: PlanningEntity) => void;
  setLatestAlgorithmRequestVersion: (value: number) => void;
  setPlanningsOperationDeliveryByDeliveryIdEntity: (value: PlanningsOperationDeliveryByDeliveryIdEntity) => void;
  addEmptyCyclePlanningsOperationDeliveryByDeliveryIdEntity: (value: number) => void;
  removeEmptyCyclePlanningsOperationDeliveryByDeliveryIdEntity: (deliveryId: number, cycleIndex: number) => void;
  refreshRunningEntities: () => void;
  handlePlanningResponse: (response: PlanningResponseEntity | PlanningResponseV5Entity, message: string, resetQuery: () => void,) => void;
  handleErrorResponse: (error: AxiosError<{ message: string; }>, resetQuery: () => void,) => void;
  setOrderIds: (value: number[]) => void;
  addOrderIds: (value: number[]) => void;
  removeOrderIds: (value: number[]) => void;
  resetOrderIds: () => void;
  setAllocatedOrderIds: (value: number[]) => void;
  addAllocatedOrderIds: (value: number[]) => void;
  removeAllocatedOrderIds: (value: number[]) => void;
  resetAllocatedOrderIds: () => void;
  setSelectedOrderIds: (value: number[]) => void;
  addSelectedOrderIds: (value: number) => void;
  bulkAddSelectedOrderIds: (value: number[]) => void;
  removeSelectedOrderIds: (value: number) => void;
  bulkRemoveSelectedOrderIds: (value: number[]) => void;
  resetSelectedOrderIds: () => void;
  setNotAllocReasonState: (value: NotAllocReasonState) => void;
  setDriverEntities: (value: PlanningsDriverEntity[]) => void;
  setTruckEntities: (value: PlanningsTruckEntity[]) => void;
  setPlanningsNotAllocReasons: (value: PlanningsNotAllocReason[]) => void;
  resetPlanningsNotAllocReasons: () => void;
  addAllocateHistoryEntity: (value: AllocateHistoryEntity) => void;
  resetAllocateHistoryEntity: () => void;
  generateRandomString: (charCount: number) => string;
};

const initialState: ContextEntity = {
  startOn: '',
  endOn: '',
  isLoading: false,
  deliveryEntities: [],
  calculating: false,
  selectedCycleIndexes: [],
  truckDisplayStatus: 'すべて表示',
  selectedGroupEntities: [],
  allDeliverySelected: false,
  orderSearchKw: '',
  currentlyOrderSearching: false,
  orderSearchConditions: [searchConditionInitialValue],
  planningEntity: null,
  latestAlgorithmRequestVersion: 0,
  planningsOperationDeliveryByDeliveryIdEntity: null,
  orderIds: [],
  allocatedOrderIds: [],
  selectedOrderIds: [],
  notAllocState: { sessionId: null, notAllocOrderLength: 0, disabledOrders: [] },
  driverEntities: [],
  truckEntities: [],
  notAllocReasons: [],
  allocateHistories: [],
};

const handlers: Record<
  string,
  (state: ContextEntity, action: Action) => ContextEntity
> = {
  SET_INITIAL_STATE: (state: ContextEntity): ContextEntity => ({
    ...initialState,
    startOn: state.startOn,
    endOn: state.endOn,
    latestAlgorithmRequestVersion: state.latestAlgorithmRequestVersion,
    driverEntities: state.driverEntities,
    truckEntities: state.truckEntities,
    deliveryEntities: state.deliveryEntities,
  }),
  SET_START_ON: (state: ContextEntity, { value }: SetStartOn): ContextEntity => ({
    ...state,
    startOn: value,
  }),
  SET_END_ON: (state: ContextEntity, { value }: SetEndOn): ContextEntity => ({
    ...state,
    endOn: value,
  }),
  SET_IS_LOADING: (state: ContextEntity, { value }: SetIsLoading): ContextEntity => ({
    ...state,
    isLoading: value
  }),
  SET_DELIVERY_ENTITIES: (state: ContextEntity, { value }: SetDeliveryEntities): ContextEntity => ({
    ...state,
    deliveryEntities: value,
  }),
  SET_CALCULATING: (state: ContextEntity, { value }: SetIsLoading): ContextEntity => ({
    ...state,
    calculating: value
  }),
  UPDATE_CYCLE_INCEX: (state: ContextEntity, { value }: UpdateCycleIndex): ContextEntity => {
    const filtered = state.selectedCycleIndexes.filter((it) => it.deliveryId !== value.deliveryId);
    return {
      ...state,
      selectedCycleIndexes: [...filtered, value],
    };
  },
  REMOVE_CYCLE_INDEX: (state: ContextEntity, { value }: RemoveCycleIndex): ContextEntity => ({
    ...state,
    selectedCycleIndexes: state.selectedCycleIndexes.filter((it) => it.deliveryId !== value),
  }),
  RESET_CYCLE_INDEXES: (state: ContextEntity): ContextEntity => ({
    ...state,
    selectedCycleIndexes: [],
  }),
  SET_TRUCK_DISPLAY_STATUS: (state: ContextEntity, { value }: SetTruckDisplayStatus): ContextEntity => ({
    ...state,
    truckDisplayStatus: value,
  }),
  SET_SELECTED_GROUP_ENTITIES: (state: ContextEntity, { value }: SetSelectedGroupEntities): ContextEntity => ({
    ...state,
    selectedGroupEntities: value,
  }),
  RESET_SELECTED_GROUP_ENTITIES: (state: ContextEntity): ContextEntity => ({
    ...state,
    selectedGroupEntities: [],
  }),
  SET_ALL_DELIVERY_SELECTED: (state: ContextEntity, { value }: SetAllDeliverySelected): ContextEntity => ({
    ...state,
    allDeliverySelected: value,
  }),
  SET_ORDER_SEARCH_KW: (state: ContextEntity, { value }: SetOrderSearchKw): ContextEntity => ({
    ...state,
    orderSearchKw: value,
  }),
  SET_CURRENTLY_ORDER_SEARCHING: (state: ContextEntity, { value }: SetCurrentlyOrderSearching): ContextEntity => ({
    ...state,
    currentlyOrderSearching: value,
  }),
  UPDATE_ORDER_SEARCH_CONDITION_LOGICAL_OPERATOR: (state: ContextEntity, { index, logicalOperator }: UpdateOrderSearchConditionLogicalOperator): ContextEntity => {
    const newSearchCondition = state.orderSearchConditions.map((it, i) => {
      if (i !== index) return it;

      return {
        ...it,
        logicalOperator,
      };
    });
    return {
      ...state,
      orderSearchConditions: newSearchCondition,
    };
  },
  UPDATE_ORDER_SEARCH_CONDITION_SEARCH_KEY: (state: ContextEntity, { index, key }: UpdateOrderSearchConditionSearchKey): ContextEntity => {
    const newSearchCondition = state.orderSearchConditions.map((it, i) => {
      if (i !== index) return it;

      return {
        ...it,
        key,
        operator: undefined,
        value: undefined,
      };
    });
    return {
      ...state,
      orderSearchConditions: newSearchCondition,
    };
  },
  UPDATE_ORDER_SEARCH_CONDITION_SEARCH_OPERATOR: (state: ContextEntity, { index, operator }: UpdateOrderSearchConditionSearchOperator): ContextEntity => {
    const newSearchCondition = state.orderSearchConditions.map((it, i) => {
      if (i !== index) return it;

      if (['可', '不可'].includes(operator)) {
        return {
          ...it,
          operator,
          value: operator,
        };
      }
      return {
        ...it,
        operator,
      };
    });
    return {
      ...state,
      orderSearchConditions: newSearchCondition,
    };
  },
  UPDATE_ORDER_SEARCH_CONDITION_SEARCH_VALUE: (state: ContextEntity, { index, value }: UpdateOrderSearchConditionSearchValue): ContextEntity => {
    const newSearchCondition = state.orderSearchConditions.map((it, i) => {
      if (i !== index) return it;

      return {
        ...it,
        value,
      };
    });
    return {
      ...state,
      orderSearchConditions: newSearchCondition,
    };
  },
  ADD_ORDER_SEARCH_CONDITIONS: (state: ContextEntity): ContextEntity => ({
    ...state,
    orderSearchConditions: [
      ...state.orderSearchConditions,
      searchConditionInitialValue,
    ],
  }),
  DELETE_ORDER_SEARCH_CONDITIONS: (state: ContextEntity, { index }: DeleteOrderSearchConditions): ContextEntity => ({
    ...state,
    orderSearchConditions: state.orderSearchConditions.filter((it, i) => i !== index),
  }),
  SET_ORDER_SEARCH_CONDITIONS: (state: ContextEntity, { value }: SetOrderSearchConditions): ContextEntity => ({
    ...state,
    orderSearchConditions: value,
  }),
  SET_PLANNING_ENTITY: (state: ContextEntity, { value }: SetPlanningEntity): ContextEntity => ({
    ...state,
    planningEntity: value,
  }),
  SET_LATEST_ALGORITHM_REQUEST_VERSION: (state: ContextEntity, { value }: SetLatestAlgorithmRequestVersion): ContextEntity => ({
    ...state,
    latestAlgorithmRequestVersion: value,
  }),
  SET_PLANNINGS_OPERATION_DELIVERY_BY_DELIVERY_ID_ENTITY: (state: ContextEntity, { value }: SetPlanningsOperationDeliveryByDeliveryIdEntity): ContextEntity => {
    if (!state.planningsOperationDeliveryByDeliveryIdEntity) {
      return {
        ...state,
        planningsOperationDeliveryByDeliveryIdEntity: value,
      };
    }
    Object.keys(state.planningsOperationDeliveryByDeliveryIdEntity).forEach((key) => {
      const oldDelivery = state.planningsOperationDeliveryByDeliveryIdEntity[key];
      const newDelivery = value[key];
      if (oldDelivery && newDelivery) {
        for (let i = newDelivery.cycles.length; i < oldDelivery.cycles.length; i++) {
          newDelivery.addEmptyCycle();
        }
      }
    });
    return {
      ...state,
      planningsOperationDeliveryByDeliveryIdEntity: value,
    };
  },
  ADD_EMPTY_CYCLE_PLANNINGS_OPERATION_DELIVERY_BY_DELIVERY_ID_ENTITY: (state: ContextEntity, { value }: AddEmptyCyclePlanningsOperationDeliveryByDeliveryIdEntity): ContextEntity => {
    const delivery = state.planningsOperationDeliveryByDeliveryIdEntity[`${value}`];
    if (delivery) {
      delivery.addEmptyCycle();
      state.planningsOperationDeliveryByDeliveryIdEntity[`${value}`] = delivery;
    }
    return state;
  },
  REMOVE_EMPTY_CYCLE_PLANNINGS_OPERATION_DELIVERY_BY_DELIVERY_ID_ENTITY: (state: ContextEntity, { deliveryId, cycleIndex }: RemoveEmptyCyclePlanningsOperationDeliveryByDeliveryIdEntity): ContextEntity => {
    const delivery = state.planningsOperationDeliveryByDeliveryIdEntity[`${deliveryId}`];
    if (delivery) {
      delivery.removeEmptyCycle(cycleIndex);
      state.planningsOperationDeliveryByDeliveryIdEntity[`${deliveryId}`] = delivery;
    }
    return state;
  },
  SET_ORDER_IDS: (state: ContextEntity, { value }: SetOrderIds): ContextEntity => ({
    ...state,
    orderIds: value,
  }),
  ADD_ORDER_IDS: (state: ContextEntity, { value }: AddOrderIds): ContextEntity => ({
    ...state,
    orderIds: arrayUtil.uniq<number>([...state.orderIds, ...value]),
  }),
  REMOVE_ORDER_IDS: (state: ContextEntity, { value }: RemoveOrderIds): ContextEntity => ({
    ...state,
    orderIds: state.orderIds.filter((it) => !value.includes(it)),
  }),
  RESET_ORDER_IDS: (state: ContextEntity): ContextEntity => ({
    ...state,
    orderIds: [],
  }),
  SET_ALLOCATED_ORDER_IDS: (state: ContextEntity, { value }: SetAllocatedOrderIds): ContextEntity => ({
    ...state,
    allocatedOrderIds: value,
  }),
  ADD_ALLOCATED_ORDER_IDS: (state: ContextEntity, { value }: AddAllocatedOrderIds): ContextEntity => ({
    ...state,
    allocatedOrderIds: arrayUtil.uniq<number>([...state.allocatedOrderIds, ...value]),
  }),
  REMOVE_ALLOCATED_ORDER_IDS: (state: ContextEntity, { value }: RemoveAllocatedOrderIds): ContextEntity => ({
    ...state,
    allocatedOrderIds: state.allocatedOrderIds.filter((it) => !value.includes(it)),
  }),
  RESET_ALLOCATED_ORDER_IDS: (state: ContextEntity): ContextEntity => ({
    ...state,
    allocatedOrderIds: [],
  }),
  SET_SELECTED_ORDER_IDS: (state: ContextEntity, { value }: SetSelectedOrderIds): ContextEntity => ({
    ...state,
    selectedOrderIds: arrayUtil.uniq<number>(value.sort()),
  }),
  ADD_SELECTED_ORDER_IDS: (state: ContextEntity, { value }: AddSelectedOrderIds): ContextEntity => ({
    ...state,
    selectedOrderIds: arrayUtil.uniq<number>([...state.selectedOrderIds, value].sort()),
  }),
  BULK_ADD_SELECTED_ORDER_IDS: (state: ContextEntity, { value }: BulkAddSelectedOrderIds): ContextEntity => ({
    ...state,
    selectedOrderIds: arrayUtil.uniq<number>([...state.selectedOrderIds, ...value].sort()),
  }),
  REMOVE_SELECTED_ORDER_IDS: (state: ContextEntity, { value }: RemoveSelectedOrderIds): ContextEntity => ({
    ...state,
    selectedOrderIds: arrayUtil.uniq<number>(state.selectedOrderIds.filter((id) => id !== value).sort()),
  }),
  BULK_REMOVE_SELECTED_ORDER_IDS: (state: ContextEntity, { value }: BulkRemoveSelectedOrderIds): ContextEntity => ({
    ...state,
    selectedOrderIds: arrayUtil.uniq<number>(state.selectedOrderIds.filter((id) => !value.includes(id)).sort()),
  }),
  RESET_SELECTED_ORDER_IDS: (state: ContextEntity): ContextEntity => ({
    ...state,
    selectedOrderIds: [],
  }),
  SET_NOT_ALLOC_REASON_STATE: (state: ContextEntity, { value }: SetNotAllocReasonState): ContextEntity => ({
    ...state,
    notAllocState: value,
  }),
  SET_DRIVER_ENTITIES: (state: ContextEntity, { value }: SetDriverEntities): ContextEntity => ({
    ...state,
    driverEntities: value,
  }),
  SET_TRUCK_ENTITIES: (state: ContextEntity, { value }: SetTruckEntities): ContextEntity => ({
    ...state,
    truckEntities: value,
  }),
  SET_PLANNINGS_NOT_ALLOC_REASONS: (state: ContextEntity, { value }: SetPlanningsNotAllocReasons): ContextEntity => ({
    ...state,
    notAllocReasons: value,
  }),
  RESET_PLANNINGS_NOT_ALLOC_REASONS: (state: ContextEntity): ContextEntity => ({
    ...state,
    notAllocReasons: [],
  }),
  ADD_ALLOCATE_HISTORY_ENTITY: (state: ContextEntity, { value }: AddAllocateHistoryEntity): ContextEntity => ({
    ...state,
    allocateHistories: [value, ...state.allocateHistories],
  }),
  RESET_ALLOCATE_HISTORY_ENTITY: (state: ContextEntity): ContextEntity => ({
    ...state,
    allocateHistories: [],
  }),
};

const reducer: (state: ContextEntity, action: Action) => ContextEntity = (
  state,
  action
) => (handlers[action.type] ? handlers[action.type](state, action) : state);

/* eslint-disable @typescript-eslint/no-unused-vars */
const PlansContext: Context<ContextValueEntity> = createContext<ContextValueEntity>({
  ...initialState,
  reset(): void {},
  setStartOn(on: string): void {},
  setEndOn(on: string): void {},
  setIsLoading(value: boolean): void {},
  setDeliveryEntities(value: PlanningsDeliveryEntity[]): void {},
  setCalculating(value: boolean): void {},
  updateCycleIndex(value: SelectedCycle): void {},
  removeCycleIndex(deliveryId: number): void {},
  resetCycleIndexes(): void {},
  setTruckDisplayStatus(value: TruckDisplayStatusVo): void {},
  setSelectedGroupEntities(value: PlanningsGroupEntity[]): void {},
  resetSelectedGroupEntities(): void {},
  setAllDeliverySelected(value: boolean): void {},
  setOrderSearchKw(value: string): void {},
  setCurrentlyOrderSearching(value: boolean): void {},
  updateSearchConditionLogicalOperator(index: number, logicalOperator: LogicalOperatorVo): void {},
  updateSearchConditionSearchKey(index: number, key: string | undefined): void {},
  updateSearchConditionSearchOperator(index: number, operator: SearchOperatorVo | undefined): void {},
  updateSearchConditionSearchValue(index: number, value: string | undefined): void {},
  addSearchConditionSearchConditions(): void {},
  deleteSearchConditions(index: number): void {},
  setSearchConditions(value: OrderSearchConditionEntity[]): void {},
  planningSettings() : PlanningConditions { return null; },
  setPlanningEntity(value: PlanningEntity): void {},
  setLatestAlgorithmRequestVersion(value: number): void {},
  setPlanningsOperationDeliveryByDeliveryIdEntity(value: PlanningsOperationDeliveryByDeliveryIdEntity): void {},
  addEmptyCyclePlanningsOperationDeliveryByDeliveryIdEntity(value: number): void {},
  removeEmptyCyclePlanningsOperationDeliveryByDeliveryIdEntity(deliveryId: number, cycleIndex: number): void {},
  refreshRunningEntities(): void {},
  handlePlanningResponse(response: PlanningResponseEntity | PlanningResponseV5Entity, message: string, resetQuery: () => void,): void {},
  handleErrorResponse(error: AxiosError<{ message: string; }>, resetQuery: () => void,): void {},
  setOrderIds(value: number[]): void {},
  addOrderIds(value: number[]): void {},
  removeOrderIds(value: number[]): void {},
  resetOrderIds(): void {},
  setAllocatedOrderIds(value: number[]): void {},
  addAllocatedOrderIds(value: number[]): void {},
  removeAllocatedOrderIds(value: number[]): void {},
  resetAllocatedOrderIds(): void {},
  setSelectedOrderIds(value: number[]): void {},
  addSelectedOrderIds(value: number): void {},
  bulkAddSelectedOrderIds(value: number[]): void {},
  removeSelectedOrderIds(value: number): void {},
  bulkRemoveSelectedOrderIds(value: number[]): void {},
  resetSelectedOrderIds(): void {},
  setNotAllocReasonState(value: NotAllocReasonState): void {},
  setDriverEntities(value: PlanningsDriverEntity[]): void {},
  setTruckEntities(value: PlanningsTruckEntity[]): void {},
  setPlanningsNotAllocReasons(value: PlanningsNotAllocReason[]): void {},
  resetPlanningsNotAllocReasons(): void {},
  addAllocateHistoryEntity(value: AllocateHistoryEntity): void {},
  resetAllocateHistoryEntity(): void {},
  generateRandomString(charCount: number): string { return null; },
});
/* eslint-enable @typescript-eslint/no-unused-vars */

export type OrderSelectionContextValue = {
  orderIds: number[];
  allocatedOrderIds: number[];
  selectedOrderIds: number[];

  setOrderIds: (value: number[]) => void;
  addOrderIds: (value: number[]) => void;
  removeOrderIds: (value: number[]) => void;
  resetOrderIds: () => void;

  setAllocatedOrderIds: (value: number[]) => void;
  addAllocatedOrderIds: (value: number[]) => void;
  removeAllocatedOrderIds: (value: number[]) => void;
  resetAllocatedOrderIds: () => void;

  setSelectedOrderIds: (value: number[]) => void;
  addSelectedOrderIds: (value: number) => void;
  bulkAddSelectedOrderIds: (value: number[]) => void;
  removeSelectedOrderIds: (value: number) => void;
  bulkRemoveSelectedOrderIds: (value: number[]) => void;
  resetSelectedOrderIds: () => void;
};

/* eslint-disable @typescript-eslint/no-unused-vars */
export const OrderSelectionContext: Context<OrderSelectionContextValue> = createContext<OrderSelectionContextValue>({
  orderIds: [],
  allocatedOrderIds: [],
  selectedOrderIds: [],
  setOrderIds(value: number[]): void {},
  addOrderIds(value: number[]): void {},
  removeOrderIds(value: number[]): void {},
  resetOrderIds(): void {},
  setAllocatedOrderIds(value: number[]): void {},
  addAllocatedOrderIds(value: number[]): void {},
  removeAllocatedOrderIds(value: number[]): void {},
  resetAllocatedOrderIds(): void {},
  setSelectedOrderIds(value: number[]): void {},
  addSelectedOrderIds(value: number): void {},
  bulkAddSelectedOrderIds(value: number[]): void {},
  removeSelectedOrderIds(value: number): void {},
  bulkRemoveSelectedOrderIds(value: number[]): void {},
  resetSelectedOrderIds(): void {},
});
/* eslint-enable @typescript-eslint/no-unused-vars */

export const OrderSelectionProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const plans = useContext(PlansContext);
  const value = useMemo<OrderSelectionContextValue>(
    () => ({
      orderIds: plans.orderIds,
      allocatedOrderIds: plans.allocatedOrderIds,
      selectedOrderIds: plans.selectedOrderIds,
      setOrderIds: plans.setOrderIds,
      addOrderIds: plans.addOrderIds,
      removeOrderIds: plans.removeOrderIds,
      resetOrderIds: plans.resetOrderIds,
      setAllocatedOrderIds: plans.setAllocatedOrderIds,
      addAllocatedOrderIds: plans.addAllocatedOrderIds,
      removeAllocatedOrderIds: plans.removeAllocatedOrderIds,
      resetAllocatedOrderIds: plans.resetAllocatedOrderIds,
      setSelectedOrderIds: plans.setSelectedOrderIds,
      addSelectedOrderIds: plans.addSelectedOrderIds,
      bulkAddSelectedOrderIds: plans.bulkAddSelectedOrderIds,
      removeSelectedOrderIds: plans.removeSelectedOrderIds,
      bulkRemoveSelectedOrderIds: plans.bulkRemoveSelectedOrderIds,
      resetSelectedOrderIds: plans.resetSelectedOrderIds,
    }),
    [
      plans.orderIds,
      plans.allocatedOrderIds,
      plans.selectedOrderIds,
      plans.setOrderIds,
      plans.addOrderIds,
      plans.removeOrderIds,
      plans.resetOrderIds,
      plans.setAllocatedOrderIds,
      plans.addAllocatedOrderIds,
      plans.removeAllocatedOrderIds,
      plans.resetAllocatedOrderIds,
      plans.setSelectedOrderIds,
      plans.addSelectedOrderIds,
      plans.bulkAddSelectedOrderIds,
      plans.removeSelectedOrderIds,
      plans.bulkRemoveSelectedOrderIds,
      plans.resetSelectedOrderIds,
    ]
  );
  return (
    <OrderSelectionContext.Provider value={value}>
      {children}
    </OrderSelectionContext.Provider>
  );
};

export const PlansProvider: FC<{ children: ReactNode }> = memo((props) => {
  const { children } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [state, dispatch] = useReducer(reducer, initialState);
  const queryClient = useQueryClient();
  const [currentPath, setCurrentPath] = useState<string>();

  const location = useLocation();

  useLayoutEffect(() => {
    const newPath = location.pathname.split('/')[1];
    if (currentPath !== newPath) {
      reset();
      setCurrentPath(newPath);
    }
  }, [currentPath, location.pathname]);

  const setIsLoading = (value: boolean) => {
    dispatch({
      type: 'SET_IS_LOADING',
      value
    });
  };

  const reset = () => {
    dispatch({
      type: 'SET_INITIAL_STATE'
    });
  };

  const setStartOn = (on: string) => {
    dispatch({
      type: 'SET_START_ON',
      value: on
    });
  };

  const setEndOn = (on: string) => {
    dispatch({
      type: 'SET_END_ON',
      value: on,
    });
  };

  const setDeliveryEntities = (value: PlanningsDeliveryEntity[]) => {
    dispatch({
      type: 'SET_DELIVERY_ENTITIES',
      value,
    });
  };

  const setCalculating = (value: boolean) => {
    dispatch({
      type: 'SET_CALCULATING',
      value,
    });
  };

  const updateCycleIndex = (value: SelectedCycle) => {
    dispatch({
      type: 'UPDATE_CYCLE_INCEX',
      value,
    });
  };

  const removeCycleIndex = (deliveryId: number) => {
    dispatch({
      type: 'REMOVE_CYCLE_INDEX',
      value: deliveryId,
    });
  };

  const resetCycleIndexes = () => {
    dispatch({
      type: 'RESET_CYCLE_INDEXES',
    });
  };

  const setTruckDisplayStatus = (value: TruckDisplayStatusVo) => {
    dispatch({
      type: 'SET_TRUCK_DISPLAY_STATUS',
      value,
    });
  };

  const setSelectedGroupEntities = (value: PlanningsGroupEntity[]) => {
    dispatch({
      type: 'SET_SELECTED_GROUP_ENTITIES',
      value,
    });
  };

  const resetSelectedGroupEntities = () => {
    dispatch({
      type: 'RESET_SELECTED_GROUP_ENTITIES',
    });
  };

  const setAllDeliverySelected = (value: boolean) => {
    dispatch({
      type: 'SET_ALL_DELIVERY_SELECTED',
      value,
    });
  };

  const setOrderSearchKw = (value: string) => {
    dispatch({
      type: 'SET_ORDER_SEARCH_KW',
      value,
    });
  };

  const setCurrentlyOrderSearching = (value: boolean) => {
    dispatch({
      type: 'SET_CURRENTLY_ORDER_SEARCHING',
      value,
    });
  };

  const updateSearchConditionLogicalOperator = (index: number, logicalOperator: LogicalOperatorVo) => {
    dispatch({
      type: 'UPDATE_ORDER_SEARCH_CONDITION_LOGICAL_OPERATOR',
      index,
      logicalOperator,
    });
  };

  const updateSearchConditionSearchKey = (index: number, key: string | undefined) => {
    dispatch({
      type: 'UPDATE_ORDER_SEARCH_CONDITION_SEARCH_KEY',
      index,
      key,
    });
  };

  const updateSearchConditionSearchOperator = (index: number, operator: SearchOperatorVo | undefined) => {
    dispatch({
      type: 'UPDATE_ORDER_SEARCH_CONDITION_SEARCH_OPERATOR',
      index,
      operator,
    });
  };

  const updateSearchConditionSearchValue = (index: number, value: string | undefined) => {
    dispatch({
      type: 'UPDATE_ORDER_SEARCH_CONDITION_SEARCH_VALUE',
      index,
      value,
    });
  };

  const addSearchConditionSearchConditions = () => {
    dispatch({
      type: 'ADD_ORDER_SEARCH_CONDITIONS',
    });
  };

  const deleteSearchConditions = (index: number) => {
    dispatch({
      type: 'DELETE_ORDER_SEARCH_CONDITIONS',
      index,
    });
  };

  const setSearchConditions = (value: OrderSearchConditionEntity[]) => {
    dispatch({
      type: 'SET_ORDER_SEARCH_CONDITIONS',
      value,
    });
  };

  const planningSettings = () => {
    const json = localStorage.getItem('PlanningPresenterSettings');
    return JSON.parse(json) as unknown as PlanningConditions;
  };

  const setPlanningEntity = (value: PlanningEntity) => {
    dispatch({
      type: 'SET_PLANNING_ENTITY',
      value,
    });
  };

  const setLatestAlgorithmRequestVersion = (value: number) => {
    dispatch({
      type: 'SET_LATEST_ALGORITHM_REQUEST_VERSION',
      value,
    });
  };

  const setPlanningsOperationDeliveryByDeliveryIdEntity = (value: PlanningsOperationDeliveryByDeliveryIdEntity) => {
    dispatch({
      type: 'SET_PLANNINGS_OPERATION_DELIVERY_BY_DELIVERY_ID_ENTITY',
      value,
    });
  };

  const addEmptyCyclePlanningsOperationDeliveryByDeliveryIdEntity = (value: number) => {
    dispatch({
      type: 'ADD_EMPTY_CYCLE_PLANNINGS_OPERATION_DELIVERY_BY_DELIVERY_ID_ENTITY',
      value,
    });
  };

  const removeEmptyCyclePlanningsOperationDeliveryByDeliveryIdEntity = (deliveryId: number, cycleIndex: number) => {
    dispatch({
      type: 'REMOVE_EMPTY_CYCLE_PLANNINGS_OPERATION_DELIVERY_BY_DELIVERY_ID_ENTITY',
      deliveryId,
      cycleIndex,
    });
  };

  const refreshRunningEntities = () => {
    void queryClient.invalidateQueries(
      ['useQueryPlanningsRunnings']
    );
  };

  const setOrderIds = (value: number[]) => {
    dispatch({
      type: 'SET_ORDER_IDS',
      value,
    });
  };

  const addOrderIds = (value: number[]) => {
    dispatch({
      type: 'ADD_ORDER_IDS',
      value,
    });
  };

  const removeOrderIds = (value: number[]) => {
    dispatch({
      type: 'REMOVE_ORDER_IDS',
      value,
    });
  };

  const resetOrderIds = () => {
    dispatch({
      type: 'RESET_ORDER_IDS',
    });
  };

  const setAllocatedOrderIds = (value: number[]) => {
    dispatch({
      type: 'SET_ALLOCATED_ORDER_IDS',
      value,
    });
  };

  const addAllocatedOrderIds = (value: number[]) => {
    dispatch({
      type: 'ADD_ALLOCATED_ORDER_IDS',
      value,
    });
  };

  const removeAllocatedOrderIds = (value: number[]) => {
    dispatch({
      type: 'REMOVE_ALLOCATED_ORDER_IDS',
      value,
    });
  };

  const resetAllocatedOrderIds = () => {
    dispatch({
      type: 'RESET_ALLOCATED_ORDER_IDS',
    });
  };

  const setSelectedOrderIds = (value: number[]) => {
    dispatch({
      type: 'SET_SELECTED_ORDER_IDS',
      value,
    });
  };

  const addSelectedOrderIds = (value: number) => {
    dispatch({
      type: 'ADD_SELECTED_ORDER_IDS',
      value,
    });
  };

  const bulkAddSelectedOrderIds = (value: number[]) => {
    dispatch({
      type: 'BULK_ADD_SELECTED_ORDER_IDS',
      value,
    });
  };

  const removeSelectedOrderIds = (value: number) => {
    dispatch({
      type: 'REMOVE_SELECTED_ORDER_IDS',
      value,
    });
  };

  const bulkRemoveSelectedOrderIds = (value: number[]) => {
    dispatch({
      type: 'BULK_REMOVE_SELECTED_ORDER_IDS',
      value,
    });
  };

  const resetSelectedOrderIds = () => {
    dispatch({
      type: 'RESET_SELECTED_ORDER_IDS',
    });
  };

  const setNotAllocReasonState = (value: NotAllocReasonState) => {
    dispatch({
      type: 'SET_NOT_ALLOC_REASON_STATE',
      value,
    });
  };

  const setDriverEntities = (value: PlanningsDriverEntity[]) => {
    dispatch({
      type: 'SET_DRIVER_ENTITIES',
      value,
    });
  };

  const setTruckEntities = (value: PlanningsTruckEntity[]) => {
    dispatch({
      type: 'SET_TRUCK_ENTITIES',
      value,
    });
  };

  const setPlanningsNotAllocReasons = (value: PlanningsNotAllocReason[]) => {
    dispatch({
      type: 'SET_PLANNINGS_NOT_ALLOC_REASONS',
      value,
    });
  };

  const resetPlanningsNotAllocReasons = () => {
    dispatch({
      type: 'RESET_PLANNINGS_NOT_ALLOC_REASONS',
    });
  };

  const addAllocateHistoryEntity = (value: AllocateHistoryEntity) => {
    dispatch({
      type: 'ADD_ALLOCATE_HISTORY_ENTITY',
      value,
    });
  };

  const resetAllocateHistoryEntity = () => {
    dispatch({
      type: 'RESET_ALLOCATE_HISTORY_ENTITY',
    });
  };

  const deliveryCache = usePlanningsDeliveriesCache();
  const legRunCache = usePlanningsDeliveriesWithLegRunsCache();

  const handlePlanningResponse = (
    response: PlanningResponseEntity | PlanningResponseV5Entity,
    message: string,
    resetQuery: () => void,
  ) => {
    if ('version' in response && response.version === 5) {
      if (response.deliveries.data) {
        legRunCache.updateDeliveries(response.deliveries.data);
      } else {
        void queryClient.invalidateQueries({ queryKey: [PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY] });
      }
    } else {
      deliveryCache.updateDeliveries(response.deliveries.data as unknown as PlanningsDeliveryEntity[]);
    }
    setLatestAlgorithmRequestVersion(response.latest_algorithm_request_version);
    resetQuery();

    if (message) {
      enqueueSnackbar(message);
    }

    const remainedOrderIds = response.delete_order_ids_from_operations ? [
      ...response.not_allocated_order_ids,
      ...response.delete_order_ids_from_operations,
    ] : response.not_allocated_order_ids;

    addOrderIds(remainedOrderIds);
    removeAllocatedOrderIds(remainedOrderIds);

    if (response.messages && response.messages.length > 0) {
      enqueueSnackbar(response.messages.join(', '));
    }
    if (response.not_allocated_order_ids && response.not_allocated_order_ids.length) {
      remainedOrderIds.forEach((it) => {
        addSelectedOrderIds(it);
      });
      if (response.session_id) {
        setNotAllocReasonState({
          sessionId: response.session_id,
          notAllocOrderLength: response.not_allocated_order_ids.length,
          disabledOrders: response.disabled_order_ids,
        });
        enqueueSnackbar(`${response.not_allocated_order_ids.length.toLocaleString()}件が割り当たりませんでした。割当たらない理由を確認してください(最大100件まで)。`);
      } else {
        enqueueSnackbar(`${response.not_allocated_order_ids.length.toLocaleString()}件が割り当たりませんでした。`);
      }
    } else {
      setNotAllocReasonState({ sessionId: null, notAllocOrderLength: 0, disabledOrders: [] });
    }
    setCalculating(false);
  };

  const handleErrorResponse = (error: AxiosError<{ message: string; }>, resetQuery: () => void) => {
    resetQuery();

    if (error.response?.status === 400 && error.response?.data?.message) {
      enqueueSnackbar(error.response.data.message, { variant: 'error' });
    } else {
      enqueueSnackbar(fiveZeroZeroErrorMessage, { variant: 'error' });
    }
    setCalculating(false);
  };

  const generateRandomString = (charCount = 7): string => {
    const str = Math.random().toString(36).substring(2).slice(-charCount);
    return str.length < charCount ? str + 'a'.repeat(charCount - str.length) : str;
  };

  const value: ContextValueEntity = useMemo(
    () => ({
      reset,
      setIsLoading,
      setStartOn,
      setEndOn,
      setDeliveryEntities,
      setCalculating,
      updateCycleIndex,
      removeCycleIndex,
      resetCycleIndexes,
      setTruckDisplayStatus,
      setSelectedGroupEntities,
      resetSelectedGroupEntities,
      setAllDeliverySelected,
      setOrderSearchKw,
      setCurrentlyOrderSearching,
      updateSearchConditionLogicalOperator,
      updateSearchConditionSearchKey,
      updateSearchConditionSearchOperator,
      updateSearchConditionSearchValue,
      addSearchConditionSearchConditions,
      deleteSearchConditions,
      setSearchConditions,
      planningSettings,
      setPlanningEntity,
      setLatestAlgorithmRequestVersion,
      setPlanningsOperationDeliveryByDeliveryIdEntity,
      addEmptyCyclePlanningsOperationDeliveryByDeliveryIdEntity,
      removeEmptyCyclePlanningsOperationDeliveryByDeliveryIdEntity,
      refreshRunningEntities,
      handlePlanningResponse,
      handleErrorResponse,
      setOrderIds,
      addOrderIds,
      removeOrderIds,
      resetOrderIds,
      setAllocatedOrderIds,
      addAllocatedOrderIds,
      removeAllocatedOrderIds,
      resetAllocatedOrderIds,
      setSelectedOrderIds,
      addSelectedOrderIds,
      bulkAddSelectedOrderIds,
      removeSelectedOrderIds,
      bulkRemoveSelectedOrderIds,
      resetSelectedOrderIds,
      setNotAllocReasonState,
      setDriverEntities,
      setTruckEntities,
      setPlanningsNotAllocReasons,
      resetPlanningsNotAllocReasons,
      addAllocateHistoryEntity,
      resetAllocateHistoryEntity,
      generateRandomString,
      ...state
    }),
    [state]
  );

  return (
    <PlansContext.Provider
      value={value}
    >
      <OrderSelectionProvider>
        {children}
      </OrderSelectionProvider>
    </PlansContext.Provider>
  );
});

export default PlansContext;
